import {
  DEBUG_GLOBAL_PERMISSION,
  DEBUG_PERMISSION,
  type roles,
} from "@shared/permissions";
import { getApiBaseUrl } from "./api-config";
import { authClient } from "./auth-client";

// Client for the role-gated Debug API (worker/index.ts /api/debug/*). Mirrors
// the fetch/error conventions of ./account. These endpoints 404 for users
// without the `debug` permission (shared/permissions.ts).

/** Whether a user with this role may use the Debug features (#201). */
export function canAccessDebug(role: string | null | undefined): boolean {
  return authClient.admin.checkRolePermission({
    // Typed as a known role name, but it's a raw DB value: Better Auth handles
    // comma-separated lists and unknown roles (no permissions) at runtime.
    role: (role ?? "user") as keyof typeof roles,
    permissions: DEBUG_PERMISSION,
  });
}

/**
 * Whether a user with this role may use Debug actions that affect every
 * account — deleting global cache entries (admin only, #212).
 */
export function canManageGlobalDebug(role: string | null | undefined): boolean {
  return authClient.admin.checkRolePermission({
    role: (role ?? "user") as keyof typeof roles,
    permissions: DEBUG_GLOBAL_PERMISSION,
  });
}

/** Error carrying the server's HTTP status. */
export class DebugApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "DebugApiError";
    this.status = status;
  }
}

function apiUrl(path: string): URL {
  return new URL(path, getApiBaseUrl() || location.origin);
}

async function apiJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(apiUrl(path), { credentials: "include", ...init });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new DebugApiError(
      res.status,
      (body.error as string) ?? `Request failed (${res.status}).`,
    );
  }
  return body as T;
}

export interface DebugDoState {
  now: number;
  version: number;
  alarmScheduledAt: number | null;
  alarmInMs: number | null;
  liveSockets: number;
  // `retries` is the max per-entry retry count; `deferred` counts entries
  // currently parked by their own backoff (retry state is per-item, #135).
  pendingCategorization: { count: number; retries: number; deferred: number };
  pendingIngredientAnalysis: {
    count: number;
    lineCount: number;
    retries: number;
    deferred: number;
    firstIds: string[];
  };
  recipes: {
    storedKeys: number;
    live: number;
    deleted: number;
    fullyAnalysed: number;
    partlyAnalysed: number;
    unanalysed: number;
  };
}

export interface DebugState {
  identity: {
    userId: string;
    email: string;
    sessionAccountId: string;
    dbAccountId: string | null;
    match: boolean;
  };
  sessionAccountDO: DebugDoState;
  dbAccountDO?: DebugDoState;
}

export interface IconCacheEntry {
  groupAs: string;
  icon: string;
  updatedAt: number;
}

export interface IconCachePage {
  mappings: IconCacheEntry[];
  nextOffset: number;
  hasMore: boolean;
}

export interface AiCachePage {
  keys: { name: string }[];
  cursor: string | null;
  listComplete: boolean;
}

export type AiCachePrefix = "cat:v2:" | "split:" | "ingr:";

export function getDebugState(): Promise<DebugState> {
  return apiJson<DebugState>("/api/debug/state");
}

export function triggerDrain(): Promise<{ ok: true }> {
  return apiJson("/api/debug/drain", { method: "POST" });
}

/** Reset and re-queue every recipe's ingredient analysis for this account. */
export function reanalyseAllRecipes(): Promise<{ recipes: number }> {
  return apiJson("/api/debug/reanalyse-recipes", { method: "POST" });
}

/** Delete all icon mappings, or all matching `search`. One D1 statement. */
export async function deleteAllIconCache(search?: string): Promise<number> {
  const params = new URLSearchParams();
  if (search) params.set("q", search);
  const qs = params.toString();
  const res = await apiJson<{ deleted: number }>(
    `/api/debug/icon-cache${qs ? `?${qs}` : ""}`,
    { method: "DELETE" },
  );
  return res.deleted;
}

/**
 * Delete all AI cache entries under `prefix`, or all matching `search`. KV has
 * no bulk delete, so the endpoint deletes one bounded page per call; we loop
 * on its `cursor` until `done`, accumulating the total deleted.
 */
export async function deleteAllAiCache(
  prefix: AiCachePrefix,
  search?: string,
): Promise<number> {
  let deleted = 0;
  let cursor: string | undefined;
  for (;;) {
    const params = new URLSearchParams({ prefix });
    if (search) params.set("q", search);
    if (cursor) params.set("cursor", cursor);
    const res = await apiJson<{
      deleted: number;
      done: boolean;
      cursor: string | null;
    }>(`/api/debug/ai-cache?${params.toString()}`, { method: "DELETE" });
    deleted += res.deleted;
    if (res.done || !res.cursor) break;
    cursor = res.cursor;
  }
  return deleted;
}

export function listIconCache(
  offset = 0,
  search?: string,
): Promise<IconCachePage> {
  const params = new URLSearchParams({ offset: String(offset) });
  if (search) params.set("q", search);
  return apiJson<IconCachePage>(`/api/debug/icon-cache?${params.toString()}`);
}

export function deleteIconCacheEntry(groupAs: string): Promise<{ ok: true }> {
  return apiJson(`/api/debug/icon-cache/${encodeURIComponent(groupAs)}`, {
    method: "DELETE",
  });
}

export function listAiCache(
  prefix: AiCachePrefix,
  cursor?: string,
  search?: string,
): Promise<AiCachePage> {
  const params = new URLSearchParams({ prefix });
  if (cursor) params.set("cursor", cursor);
  if (search) params.set("q", search);
  return apiJson<AiCachePage>(`/api/debug/ai-cache?${params.toString()}`);
}

export function deleteAiCacheEntry(key: string): Promise<{ ok: true }> {
  return apiJson(`/api/debug/ai-cache/${encodeURIComponent(key)}`, {
    method: "DELETE",
  });
}
